import { supabase } from './client';
import { getTenantIdFromSession } from './tenantScoping';
import { tenantAPI } from '@/services/api/tenants';

export interface Technician {
  id: string;
  full_name: string;
  phone_number?: string;
  skill_level: number; // 1=High, 2=Medium, 3=Low
  email?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Zone assignments
  assigned_zone_ids?: string[];
  assigned_zone_names?: string[];
  // Work schedule fields
  monday_enabled?: boolean;
  monday_start_time?: string;
  monday_end_time?: string;
  tuesday_enabled?: boolean;
  tuesday_start_time?: string;
  tuesday_end_time?: string;
  wednesday_enabled?: boolean;
  wednesday_start_time?: string;
  wednesday_end_time?: string;
  thursday_enabled?: boolean;
  thursday_start_time?: string;
  thursday_end_time?: string;
  friday_enabled?: boolean;
  friday_start_time?: string;
  friday_end_time?: string;
  saturday_enabled?: boolean;
  saturday_start_time?: string;
  saturday_end_time?: string;
  sunday_enabled?: boolean;
  sunday_start_time?: string;
  sunday_end_time?: string;
}

export interface CreateTechnicianData {
  full_name: string;
  phone_number?: string;
  skill_level: number;
  email?: string;
  is_active?: boolean;
  notes?: string;
  assigned_zone_ids?: string[];
  // Work schedule fields
  monday_enabled?: boolean;
  monday_start_time?: string;
  monday_end_time?: string;
  tuesday_enabled?: boolean;
  tuesday_start_time?: string;
  tuesday_end_time?: string;
  wednesday_enabled?: boolean;
  wednesday_start_time?: string;
  wednesday_end_time?: string;
  thursday_enabled?: boolean;
  thursday_start_time?: string;
  thursday_end_time?: string;
  friday_enabled?: boolean;
  friday_start_time?: string;
  friday_end_time?: string;
  saturday_enabled?: boolean;
  saturday_start_time?: string;
  saturday_end_time?: string;
  sunday_enabled?: boolean;
  sunday_start_time?: string;
  sunday_end_time?: string;
}

export interface UpdateTechnicianData {
  full_name?: string;
  phone_number?: string;
  skill_level?: number;
  email?: string;
  is_active?: boolean;
  notes?: string;
  assigned_zone_ids?: string[];
  // Work schedule fields
  monday_enabled?: boolean;
  monday_start_time?: string;
  monday_end_time?: string;
  tuesday_enabled?: boolean;
  tuesday_start_time?: string;
  tuesday_end_time?: string;
  wednesday_enabled?: boolean;
  wednesday_start_time?: string;
  wednesday_end_time?: string;
  thursday_enabled?: boolean;
  thursday_start_time?: string;
  thursday_end_time?: string;
  friday_enabled?: boolean;
  friday_start_time?: string;
  friday_end_time?: string;
  saturday_enabled?: boolean;
  saturday_start_time?: string;
  saturday_end_time?: string;
  sunday_enabled?: boolean;
  sunday_start_time?: string;
  sunday_end_time?: string;
}

export const technicianAPI = {
  // Get all technicians with zone information
  async getTechniciansWithCampaigns(): Promise<Technician[]> {
    const tenantId = await getTenantIdFromSession(supabase);

    // Hide soft-deleted technicians (is_active=false). The "Delete" action sets
    // is_active=false instead of issuing a hard DELETE because FKs from
    // technician_assignments and recall_tracking_metadata block hard deletes.
    let query = supabase.schema('prod').from('technicians').select('*').eq('is_active', true);

    // Apply tenant filtering if tenant_id column exists and tenant ID is available
    // Note: This will work if technicians table has tenant_id column
    // Otherwise, RLS policies on technician_assignments will handle filtering
    if (tenantId) {
      // Try to filter by tenant_id if column exists
      // If column doesn't exist, this will be ignored and RLS will handle it
      try {
        query = query.eq('tenant_id', tenantId);
      } catch {
        // Column might not exist, continue without tenant_id filter
        // RLS policies will handle tenant scoping
        // eslint-disable-next-line no-console
        console.warn('tenant_id column may not exist on technicians, relying on RLS');
      }
    }

    query = query.order('full_name');

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Fetch zones for tenant to populate zone names
    const zones = await this.getZones();
    const zoneMap = new Map(zones.map((zone: any) => [zone.id, zone.name || zone.zone_name]));

    // Transform technicians and populate zone names
    return (data || []).map((tech: any) => {
      // Handle case where assigned_zone_ids might be an array column
      const assignedZoneIds = Array.isArray(tech.assigned_zone_ids)
        ? tech.assigned_zone_ids
        : tech.assigned_zone_ids
          ? [tech.assigned_zone_ids]
          : [];

      // Map zone IDs to zone names
      const assignedZoneNames = assignedZoneIds
        .map((zoneId: string) => zoneMap.get(zoneId))
        .filter((name: string | undefined) => name) as string[];

      return {
        ...tech,
        assigned_zone_ids: assignedZoneIds,
        assigned_zone_names: assignedZoneNames
      };
    });
  },

  // Get technicians by skill level
  async getTechniciansBySkillLevel(skillLevel: number): Promise<Technician[]> {
    try {
      const tenantId = await getTenantIdFromSession(supabase);

      let query = supabase
        .schema('prod')
        .from('technicians')
        .select('*')
        .eq('skill_level', skillLevel)
        .eq('is_active', true);

      // Apply tenant filtering if tenant_id column exists and tenant ID is available
      if (tenantId) {
        try {
          query = query.eq('tenant_id', tenantId);
        } catch {
          // Column might not exist, continue without tenant_id filter
          // eslint-disable-next-line no-console
          console.warn('tenant_id column may not exist on technicians, relying on RLS');
        }
      }

      query = query.order('full_name');

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  },

  // Get single technician
  async getTechnician(id: string): Promise<Technician | null> {
    try {
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return null;
      }

      // Fetch zones for tenant to populate zone names
      const zones = await this.getZones();
      const zoneMap = new Map(zones.map((zone: any) => [zone.id, zone.name || zone.zone_name]));

      // Handle case where assigned_zone_ids might be an array column
      const assignedZoneIds = Array.isArray(data.assigned_zone_ids)
        ? data.assigned_zone_ids
        : data.assigned_zone_ids
          ? [data.assigned_zone_ids]
          : [];

      // Map zone IDs to zone names
      const assignedZoneNames = assignedZoneIds
        .map((zoneId: string) => zoneMap.get(zoneId))
        .filter((name: string | undefined) => name) as string[];

      return {
        ...data,
        assigned_zone_ids: assignedZoneIds,
        assigned_zone_names: assignedZoneNames
      };
    } catch (error) {
      throw error;
    }
  },

  // Create new technician
  async createTechnician(technicianData: CreateTechnicianData): Promise<Technician> {
    try {
      // Get tenant_id from session (required for RLS policy)
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('Tenant ID is required to create a technician');
      }

      // Prepare insert data with tenant_id
      // Explicitly exclude computed/auto-generated fields that shouldn't be in insert

      const {
        id: _id,
        created_at: _created_at,
        updated_at: _updated_at,
        assigned_zone_names: _assigned_zone_names,
        ...dataForInsert
      } = technicianData as any;

      // Ensure assigned_zone_ids is properly formatted (array of UUIDs)
      const insertData: any = {
        ...dataForInsert,
        tenant_id: tenantId
      };

      // Convert assigned_zone_ids to proper format if provided
      if (insertData.assigned_zone_ids && Array.isArray(insertData.assigned_zone_ids)) {
        // Filter out any empty/null values and ensure they're strings
        insertData.assigned_zone_ids = insertData.assigned_zone_ids
          .filter((id: string | null | undefined) => id != null && id !== '')
          .map((id: string) => String(id));
      } else {
        // Default to empty array if not provided
        insertData.assigned_zone_ids = [];
      }

      // Include tenant_id in the insert data
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        // Log detailed error for debugging
        console.error('[createTechnician] Supabase error:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          tenantId: tenantId,
          insertDataKeys: Object.keys(insertData),
          assignedZoneIds: insertData.assigned_zone_ids
        });
        throw error;
      }

      return data;
    } catch (error) {
      // Re-throw with more context if it's not already a Supabase error
      if (error instanceof Error && !('code' in error)) {
        console.error('[createTechnician] Error:', error);
      }
      throw error;
    }
  },

  // Update technician
  async updateTechnician(id: string, updates: UpdateTechnicianData): Promise<Technician> {
    try {
      // Update technician fields directly, including assigned_zone_ids as an array column
      // Only select the fields we need to avoid unnecessary data transfer
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .update(updates)
        .eq('id', id)
        .select(
          'id,full_name,phone_number,skill_level,email,is_active,notes,assigned_zone_ids,created_at,updated_at'
        )
        .single();

      if (error) {
        throw error;
      }

      // Return data without zone names - they'll be populated from cache in the hook
      // This avoids an expensive zone fetch on every update
      return data;
    } catch (error) {
      throw error;
    }
  },

  // Delete technician (soft delete — preserves historical assignments and avoids FK conflicts)
  async deleteTechnician(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .schema('prod')
        .from('technicians')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        throw error;
      }
    } catch (error) {
      throw error;
    }
  },

  // Zone Management Methods - Get zones for current tenant from tenant_zones
  async getZones(): Promise<any[]> {
    try {
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        return [];
      }

      // Use tenantAPI.getTenantZones which uses admin client to bypass RLS
      const zones = await tenantAPI.getTenantZones(tenantId);

      // Transform to match expected format
      return zones.map(zone => ({
        id: zone.id,
        name: zone.zone_name,
        zone_name: zone.zone_name,
        slug: zone.zone_slug,
        zone_slug: zone.zone_slug,
        description: zone.description,
        region: zone.region,
        country: zone.country,
        status: zone.status,
        metadata: zone.metadata,
        created_at: zone.created_at,
        updated_at: zone.updated_at
      }));
    } catch (error) {
      throw error;
    }
  },

  async getZoneById(zoneId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .schema('prod')
        .from('zones')
        .select('*')
        .eq('id', zoneId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async getTechniciansByZone(zoneId: string): Promise<Technician[]> {
    try {
      const tenantId = await getTenantIdFromSession(supabase);

      // Get all technicians for tenant and filter by zone ID in assigned_zone_ids array
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .select('*')
        .eq('tenant_id', tenantId)
        .contains('assigned_zone_ids', [zoneId]);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  },

  // Note: Zone assignment/unassignment is handled via updateTechnician
  // by updating the assigned_zone_ids array column directly
  async assignTechnicianToZone(technicianId: string, zoneId: string): Promise<void> {
    try {
      // Get current technician
      const technician = await this.getTechnician(technicianId);
      if (!technician) {
        throw new Error('Technician not found');
      }

      // Add zone to assigned_zone_ids array if not already present
      const currentZoneIds = technician.assigned_zone_ids || [];
      if (!currentZoneIds.includes(zoneId)) {
        await this.updateTechnician(technicianId, {
          assigned_zone_ids: [...currentZoneIds, zoneId]
        });
      }
    } catch (error) {
      throw error;
    }
  },

  async unassignTechnicianFromZone(technicianId: string, zoneId: string): Promise<void> {
    try {
      // Get current technician
      const technician = await this.getTechnician(technicianId);
      if (!technician) {
        throw new Error('Technician not found');
      }

      // Remove zone from assigned_zone_ids array
      const currentZoneIds = technician.assigned_zone_ids || [];
      await this.updateTechnician(technicianId, {
        assigned_zone_ids: currentZoneIds.filter((id: string) => id !== zoneId)
      });
    } catch (error) {
      throw error;
    }
  },

  async bulkAssignTechniciansToZone(technicianIds: string[], zoneId: string): Promise<void> {
    try {
      // Update each technician's assigned_zone_ids array
      await Promise.all(technicianIds.map(id => this.assignTechnicianToZone(id, zoneId)));
    } catch (error) {
      throw error;
    }
  },

  async bulkUnassignTechniciansFromZone(technicianIds: string[], zoneId: string): Promise<void> {
    try {
      // Update each technician's assigned_zone_ids array
      await Promise.all(technicianIds.map(id => this.unassignTechnicianFromZone(id, zoneId)));
    } catch (error) {
      throw error;
    }
  },

  // Work Schedule Management Methods
  async getTechnicianWorkSchedules(technicianId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('technician_work_schedules')
        .select('*')
        .eq('technician_id', technicianId)
        .order('schedule_type', { ascending: true });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  },

  async saveTechnicianWorkSchedule(technicianId: string, scheduleData: any): Promise<any> {
    try {
      // Remove custom schedule fields from scheduleData
      const {
        custom_schedule_name,
        custom_schedule_start_date,
        custom_schedule_end_date,
        custom_schedule_start_time,
        custom_schedule_end_time,
        custom_schedule_monday_enabled,
        custom_schedule_tuesday_enabled,
        custom_schedule_wednesday_enabled,
        custom_schedule_thursday_enabled,
        custom_schedule_friday_enabled,
        custom_schedule_saturday_enabled,
        custom_schedule_sunday_enabled,
        ...workScheduleFields
      } = scheduleData;

      // Update technician directly with work schedule fields
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .update(workScheduleFields)
        .eq('id', technicianId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async checkTechnicianAvailability(
    technicianId: string,
    date: string,
    time?: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('is_technician_available', {
        p_technician_id: technicianId,
        p_date: date,
        p_time: time || '08:00:00'
      });

      if (error) {
        throw error;
      }

      return data || false;
    } catch (error) {
      throw error;
    }
  },

  async getTechnicianWorkingHours(technicianId: string, date: string): Promise<any[]> {
    try {
      const { data, error } = await supabase.rpc('get_technician_working_hours', {
        p_technician_id: technicianId,
        p_date: date
      });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }
};
